// /:partnerId/subscribe — gestión de plan hosteada (§6.2 del contrato OTT).
//
// La OTT manda al usuario con ?client_id&return_url&state&id_token_hint, y
// opcionalmente uno de los dos atajos de plan:
//   &plan_id=…            → deja ese plan marcado en la grilla (contrato v1.1)
//   &checkout_plan_id=…   → el usuario ya eligió en la landing: va derecho al pago
// Permite: adquirir / upgrade (cobro), downgrade a Free (sin cobro) y cancelar.
// Al terminar, vuelve a return_url con ?state&status=success|cancelled|failed.
//
// Auth: `id_token_hint` (verificado server-side) mintea una sesión corta → SIN
// re-login. Si falta o es inválido, caemos a login email+password.
//
// El enlace AFIRMA una cuenta; no prueba que quien lo abre sea su titular (un
// enlace ajeno abierto acá hacía pagar dentro de la cuenta de otro, sin aviso).
// Por eso (lib/account-guard.ts):
//   - el hint sale de la barra de direcciones apenas se lee;
//   - viaja junto a la constancia de este navegador (lib/browser-identity.ts):
//     si son cuentas distintas el backend no da sesión → pantalla de bloqueo;
//   - la cuenta se muestra siempre ("Cuenta: ju***@gmail.com");
//   - si la identidad solo la afirma el enlace, mirar el plan está permitido
//     pero CUALQUIER cambio pasa antes por "¿es tu cuenta?" (ver `guarded`).
//
// `status` es solo una pista de UX; la fuente de verdad del acceso es
// /entitlements/check (la OTT re-consulta). "success" = la acción del usuario se
// completó (incluye cancelar/downgrade); "cancelled" = se fue sin cambios.
//
// Un pago del widget NUNCA se descarta sin confirmarlo antes: el `payment_id`
// se guarda (lib/checkout-resume.ts) y, si la página se recarga con uno en
// curso, se confirma ESE pago antes de mostrar la grilla. Mismo criterio que
// /pay — ver el encabezado de Pay.tsx. Confirmarlo también es operar sobre la
// cuenta: con la identidad sin corroborar, antes pasa por "¿es tu cuenta?".

import { FormEvent, useEffect, useRef, useState } from 'react';
import { normalizeEmail } from '@/lib/email-normalize';
import { useParams, useSearchParams } from 'react-router-dom';
import AuthShell from '@/components/AuthShell';
import { resolvePalette, useTheme } from '@/theme/ThemeProvider';
import FormField from '@/components/FormField';
import SubmitButton from '@/components/SubmitButton';
import PlanPicker from '@/components/PlanPicker';
import {
  fetchPlans,
  subscribeInit,
  loginForAccessToken,
  fetchMeSubscription,
  createCheckoutSession,
  confirmCheckoutPayment,
  redeemCoupon,
  changePlanToFree,
  cancelSubscription,
  ApiError,
  type Plan,
  type SubscribeInitResult,
} from '@/lib/api';
import { lineWidgetOrigin, paletteToLineTheme, withLineTheme } from '@/lib/lineTheme';
import { BackStepLink, FlowExitLink } from '@/components/FlowNav';
import { precheckCoupon } from '@/lib/couponCheck';
import { directCheckoutPlan, directCheckoutWarning } from '@/lib/plan-selection';
import PaymentVerifying from '@/components/PaymentVerifying';
import {
  AccountConfirmScreen,
  AccountLine,
  AccountMismatchScreen,
} from '@/components/AccountIdentity';
import {
  type MismatchReason,
  initScreen,
  needsAccountConfirmation,
  resumeDecision,
  withoutIdentityParams,
  type AccountView,
} from '@/lib/account-guard';
import { readBrowserTicket } from '@/lib/browser-identity';
import { clearCheckoutResume, readCheckoutResume, resumeKey, saveCheckoutResume } from '@/lib/checkout-resume';
import {
  CONFIRM_DELAYS,
  PAYMENT_REJECTED_MESSAGE,
  RESUME_CONFIRM_DELAYS,
  SINGLE_CONFIRM,
  checkoutOutcome,
  pollConfirm,
} from '@/lib/checkout-outcome';

const PORTAL_CLIENT_ID =
  (import.meta.env.VITE_PORTAL_CLIENT_ID as string | undefined) ?? 'cli_dev_spa';

type Current = SubscribeInitResult['current'];
const LIVE = ['active', 'trialing', 'grace_period'];
// Pasarelas que se pagan fuera del portal (redirect) en vez de en un widget.
const REDIRECT_PROVIDERS: ReadonlySet<string> = new Set(['mercadopago']);

type Step =
  | { kind: 'loading' }
  | { kind: 'login' }
  // El enlace es de otra cuenta que la de este navegador: sin sesión.
  | { kind: 'mismatch'; reason: MismatchReason }
  | { kind: 'manage'; current: Current }
  | { kind: 'pay'; plan: Plan }
  | { kind: 'widget'; plan: Plan; checkoutUrl: string; paymentId: string }
  // checking: no viene del aviso de éxito del widget, estamos averiguando.
  | { kind: 'confirming'; checking?: boolean }
  // Hay un pago sin veredicto: se verifica ESE pago, no se crea otro.
  | { kind: 'verifying'; plan: Plan; paymentId: string; origin: 'resume' | 'timeout' }
  | { kind: 'redirecting' }
  | { kind: 'done'; message: string }
  // inFlight: el pago que hay que confirmar antes de reintentar.
  | { kind: 'error'; message: string; canRetry?: boolean; inFlight?: { plan: Plan; paymentId: string } };

function fmt(amount: number, currency: string): string {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es-AR', { day: 'numeric', month: 'long' });
}

/**
 * Hasta cuando conserva el plan pago una sub dada de baja (null si renueva o
 * no hay plan vigente). Regla: la baja respeta el periodo pago y al vencer
 * pasa al plan gratuito.
 */
function endsAt(current: Current): string | null {
  if (!current || !LIVE.includes(current.status)) return null;
  if (!current.cancelled_at || !current.current_period_end) return null;
  return current.current_period_end;
}

/** Mensajes de error del canje de cupón, compartidos con /pay. */
export function couponErrorMessage(e: unknown): string {
  const a = e instanceof ApiError ? e : null;
  switch (a?.code) {
    case 'coupon_not_found':
      return 'El cupón no existe. Revisá el código.';
    case 'coupon_already_redeemed':
      return 'Ese cupón ya fue utilizado.';
    case 'coupon_revoked':
      return 'Ese cupón fue anulado.';
    case 'coupon_plan_mismatch':
      return 'Ese cupón es para otro plan.';
    case 'rate_limited':
      return 'Demasiados intentos. Esperá unos minutos y volvé a probar.';
    default:
      return a?.message ?? 'No pudimos validar el cupón.';
  }
}

export default function Subscribe(): JSX.Element {
  const { partnerId = '' } = useParams<{ partnerId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  // El hint se lee UNA vez, al montar, y se guarda acá: enseguida se lo quita
  // de la URL (efecto de abajo), así que `searchParams` deja de tenerlo.
  const [idTokenHint] = useState<string | null>(() => searchParams.get('id_token_hint'));
  const clientId = searchParams.get('client_id') ?? PORTAL_CLIENT_ID;
  const returnUrl = searchParams.get('return_url') ?? '';
  const oauthState = searchParams.get('state') ?? '';
  // `plan_id` (contractual, v1.1): deja el plan MARCADO en la grilla, el usuario
  // igual confirma. `checkout_plan_id`: el usuario ya eligió en la landing y va
  // derecho al pago. Son distintos a propósito — ver initialStep().
  const preselectPlanId = searchParams.get('plan_id');
  const checkoutPlanId = searchParams.get('checkout_plan_id');
  // Branding para tematizar el widget de Line (ver bloque `widget` abajo).
  const { state: brandingState, colorScheme } = useTheme();

  const [step, setStep] = useState<Step>({ kind: 'loading' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [customerDoc, setCustomerDoc] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [selected, setSelected] = useState<Plan | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Cuenta sobre la que se opera (null hasta identificarla).
  const [account, setAccount] = useState<AccountView | null>(null);
  // Acción en espera del "sí, es mi cuenta" (ver `guarded`).
  const [pending, setPending] = useState<{ run: () => void } | null>(null);
  // El "sí" vale para esta visita: vive en memoria, no se persiste.
  const acknowledgedRef = useRef(false);

  const tokenRef = useRef<string | null>(null);
  const currentRef = useRef<Current>(null);
  const returnOkRef = useRef<boolean>(false);
  // Clave del pago en curso guardado: partner + usuario (se conoce recién
  // después de identificar la sesión). null = no se persiste.
  const resumeKeyRef = useRef<string | null>(null);
  // Guard contra doble envío de "Activar" (ver Pay.tsx).
  const startingRef = useRef(false);

  // Saca el `id_token_hint` de la barra de direcciones (replace: tampoco queda
  // en el historial) conservando el resto de los parámetros. Así no se comparte
  // al copiar la URL. Recargar después de esto cae al login manual.
  useEffect(() => {
    const next = withoutIdentityParams(searchParams);
    if (next) setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // ÚNICO punto por el que pasa toda acción que cambia algo (pagar, canjear
  // cupón, cambiar de plan, pasar al gratuito, cancelar). Si la cuenta solo la
  // afirma el enlace, la acción queda en espera y se muestra "¿es tu cuenta?".
  function guarded(action: () => void): void {
    if (needsAccountConfirmation(account, acknowledgedRef.current)) {
      setPending({ run: action });
      return;
    }
    action();
  }

  // "No es mi cuenta": suelta la sesión del enlace y va al login manual. No
  // toca la constancia del navegador (esa habla de quién inició sesión acá).
  function switchAccount(): void {
    tokenRef.current = null;
    currentRef.current = null;
    resumeKeyRef.current = null;
    acknowledgedRef.current = false;
    setAccount(null);
    setPending(null);
    setSelected(null);
    setCustomerDoc('');
    setCouponCode('');
    setErr(null);
    setStep({ kind: 'login' });
  }

  // Aviso para los devs de la OTT (consola): explica por qué no va a haber
  // auto-redirect, en vez de degradar en silencio a la pantalla final.
  function warnIfNoReturn(returnUrlOk: boolean): void {
    if (!returnUrl) {
      console.warn(
        '[subscribe] Falta el parámetro return_url: al terminar NO se redirige a la app ' +
          '(se muestra la pantalla final). Ver §1 del anexo v1.1 del contrato.',
      );
    } else if (!returnUrlOk) {
      console.warn(
        `[subscribe] return_url "${returnUrl}" no está en la whitelist del client "${clientId}" ` +
          '(match exacto contra las redirect_uris registradas): al terminar NO se redirige a la app. ' +
          'Registrá la URL con AuthSys antes de usarla.',
      );
    }
  }

  // Vuelve a la OTT con el status, o muestra pantalla final si no hay return_url válido.
  function returnToOtt(status: 'success' | 'cancelled' | 'failed', doneMsg: string): void {
    if (returnOkRef.current && returnUrl) {
      setStep({ kind: 'redirecting' });
      try {
        const u = new URL(returnUrl);
        if (oauthState) u.searchParams.set('state', oauthState);
        u.searchParams.set('status', status);
        window.location.href = u.toString();
        return;
      } catch {
        console.warn(`[subscribe] return_url "${returnUrl}" malformada: no se puede redirigir.`);
      }
    }
    setStep({ kind: 'done', message: doneMsg });
  }

  // Entrada directa al checkout. La landing tiene dos clases de botón hacia
  // acá: "Gestionar mi plan" (el usuario todavía no eligió → grilla) y "Quiero
  // el plan Full" (ya eligió → derecho al pago). El segundo manda
  // `checkout_plan_id`; si apunta a un plan usable, salteamos la grilla.
  //
  // Devuelve el paso con el que arrancar. Cualquier rechazo cae a la grilla de
  // siempre — un botón de la landing puede quedar apuntando a un plan que
  // archivamos, y ahí el usuario tiene que poder elegir, no ver un error.
  async function initialStep(current: Current): Promise<Step> {
    if (!checkoutPlanId) return { kind: 'manage', current };
    let plans: Plan[];
    try {
      plans = await fetchPlans(partnerId);
    } catch {
      return { kind: 'manage', current };
    }
    const livePlanId = current && LIVE.includes(current.status) ? current.plan.plan_id : null;
    const direct = directCheckoutPlan({ plans, checkoutPlanId, currentPlanId: livePlanId });
    if (direct.ok) {
      // Deja el plan elegido en el estado: si el usuario vuelve al paso
      // anterior, la grilla lo muestra ya marcado.
      setSelected(direct.plan);
      return { kind: 'pay', plan: direct.plan };
    }
    const warning = directCheckoutWarning(direct.reason, checkoutPlanId);
    if (warning) console.warn(`[subscribe] ${warning}`);
    return { kind: 'manage', current };
  }

  // Primer paso tras identificar al usuario. Si quedó un pago en curso guardado
  // (la página se recargó con el widget abierto) se confirma antes que nada:
  // puede estar aprobado, y mostrar la grilla invitaría a pagarlo de nuevo.
  //
  // Confirmarlo lo acredita en la cuenta de la sesión, así que pasa por el
  // mismo "¿es tu cuenta?" que cualquier otro cambio (ver resumeDecision). La
  // cuenta se recibe explícita: el estado `account` todavía no está al día acá.
  async function enter(
    current: Current,
    view: AccountView,
    isCancelled: () => boolean = () => false,
  ): Promise<void> {
    const saved = readCheckoutResume(resumeKeyRef.current);
    if (saved) {
      let plan: Plan | undefined;
      try {
        plan = (await fetchPlans(partnerId)).find((p) => p.plan_id === saved.planId);
      } catch {
        plan = undefined;
      }
      if (isCancelled()) return;
      const decision = resumeDecision({
        planFound: plan !== undefined,
        account: view,
        acknowledged: acknowledgedRef.current,
      });
      if (plan && decision === 'confirm') {
        setSelected(plan);
        await confirmPayment(plan, saved.paymentId, 'resume');
        return;
      }
      if (plan && decision === 'ask-account') {
        // La pregunta tapa la grilla: "Volver sin hacer cambios" deja mirar el
        // plan sin confirmar nada, y el pago guardado queda para la próxima vez.
        const resumePlan = plan;
        setSelected(resumePlan);
        setPending({ run: () => void confirmPayment(resumePlan, saved.paymentId, 'resume') });
        setStep({ kind: 'manage', current });
        return;
      }
      // El plan del pago guardado ya no existe: no hay contra qué confirmarlo
      // desde acá (lo levanta la reconciliación del backend).
      clearCheckoutResume(resumeKeyRef.current);
    }
    const next = await initialStep(current);
    if (isCancelled()) return;
    setStep(next);
  }

  // Carga inicial: con id_token_hint, init server-side (sin re-login).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!idTokenHint) {
        setStep({ kind: 'login' });
        return;
      }
      try {
        const screen = initScreen(
          await subscribeInit({
            clientId,
            idTokenHint,
            returnUrl,
            browserTicket: readBrowserTicket(partnerId),
          }),
          'hint',
        );
        if (cancelled) return;
        if (screen.kind === 'mismatch') {
          // Sin sesión y sin datos de la cuenta del enlace. Solo se conserva si
          // el return_url es válido, para poder ofrecer "Volver".
          returnOkRef.current = screen.returnUrlOk;
          setStep({ kind: 'mismatch', reason: screen.reason });
          return;
        }
        const res = screen.session;
        tokenRef.current = res.access_token ?? null;
        currentRef.current = res.current;
        returnOkRef.current = res.return_url_ok;
        resumeKeyRef.current = resumeKey({ partnerId, subject: res.user_id });
        setAccount(screen.account);
        warnIfNoReturn(res.return_url_ok);
        await enter(res.current, screen.account, () => cancelled);
      } catch (e2) {
        if (cancelled) return;
        // Capacidad apagada para este partner: mensaje claro en vez del login.
        if (e2 instanceof ApiError && e2.code === 'feature_disabled') {
          setStep({
            kind: 'error',
            message: 'La gestión de planes no está disponible para tu cuenta todavía.',
          });
          return;
        }
        setStep({ kind: 'login' });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idTokenHint]);

  async function onLogin(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setLoading(true);
    setErr(null);
    try {
      const token = await loginForAccessToken({
        partnerId,
        email: normalizeEmail(email),
        password,
        clientId: PORTAL_CLIENT_ID,
        redirectUri: `${window.location.origin}/${partnerId}/subscribe`,
      });
      tokenRef.current = token;
      // Validamos return_url también en este path (misma whitelist, server-side,
      // vía subscribe_init con Bearer) para que el usuario que logueó a mano
      // tampoco quede varado sin redirect al terminar.
      // Login con contraseña en esta pantalla: la cuenta queda confirmada.
      acknowledgedRef.current = false;
      setPending(null);
      let view: AccountView = { hint: null, confirmed: true };
      try {
        const screen = initScreen(await subscribeInit({ clientId, returnUrl, bearer: token }), 'login');
        // Con Bearer no viaja constancia, así que no debería haber mismatch; si
        // lo hubiera, se trata como init fallido (fallback de abajo).
        if (screen.kind === 'mismatch') throw new ApiError('account_mismatch', 'account mismatch', 409);
        const res = screen.session;
        returnOkRef.current = res.return_url_ok;
        currentRef.current = res.current;
        resumeKeyRef.current = resumeKey({ partnerId, subject: res.user_id });
        view = screen.account;
        setAccount(view);
        warnIfNoReturn(res.return_url_ok);
      } catch {
        // Fallback (ej. client_id de otro partner): sin validación → sin redirect.
        returnOkRef.current = false;
        view = { hint: null, confirmed: true };
        setAccount(view);
        const sub = await fetchMeSubscription(token);
        currentRef.current = sub
          ? {
              status: sub.status,
              plan: {
                plan_id: sub.plan.plan_id,
                code: sub.plan.code,
                display_name: sub.plan.display_name,
                price_amount: sub.plan.price_amount,
                price_currency: sub.plan.price_currency,
              },
              current_period_end: sub.current_period_end,
              cancelled_at: sub.cancelled_at ?? null,
            }
          : null;
      }
      await enter(currentRef.current, view);
    } catch (e2) {
      const a = e2 instanceof ApiError ? e2 : null;
      setErr(
        a?.code === 'invalid_credentials' || a?.status === 401
          ? 'Email o contraseña incorrectos.'
          : a?.message ?? 'No pudimos iniciar sesión.',
      );
    } finally {
      setLoading(false);
    }
  }

  // Aplica la acción según el plan elegido vs el actual.
  async function applySelection(): Promise<void> {
    const plan = selected;
    const token = tokenRef.current;
    const current = currentRef.current;
    if (!plan || !token) {
      setErr('Elegí un plan.');
      return;
    }
    // Se queda con el que ya tiene: no hay nada que aplicar, se vuelve a la app
    // sin cambios (mismo status que salir por "Volver").
    const samePlan = current && current.plan.plan_id === plan.plan_id && LIVE.includes(current.status);
    if (samePlan) {
      returnToOtt('cancelled', 'Volviste sin cambios.');
      return;
    }
    // Downgrade / elegir Free.
    if (plan.price_amount === 0) {
      // Sin suscripcion viva no hay nada que cambiar: el gratuito es el estado
      // por defecto (el backend rechaza change_to_free sin sub activa). Se sale
      // como accion completada en vez de mostrar un error que no lo es.
      if (!current || !LIVE.includes(current.status)) {
        returnToOtt('success', 'Listo, seguís con el plan gratuito.');
        return;
      }
      guarded(() => void runChangeToFree(token, plan, current));
      return;
    }
    // Plan pago: adquirir / upgrade → cobro. Se pregunta la cuenta ANTES de
    // mostrar el paso de pago, no recién al apretar "Activar".
    guarded(() => setStep({ kind: 'pay', plan }));
  }

  async function runChangeToFree(token: string, plan: Plan, current: NonNullable<Current>): Promise<void> {
    setLoading(true);
    setErr(null);
    try {
      const res = await changePlanToFree(token, plan.plan_id);
      // Elegir el gratuito estando en un plan pago no corta lo que ya pagó:
      // conserva el plan hasta el fin del período y recién ahí pasa.
      returnToOtt(
        'success',
        res.access_until
          ? `Tu plan ${current.plan.display_name} sigue activo hasta el ${fmtDate(res.access_until)}. Después pasás al plan ${plan.display_name}.`
          : `Pasaste al plan ${plan.display_name}.`,
      );
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : 'No pudimos cambiar el plan.');
    } finally {
      setLoading(false);
    }
  }

  async function startPayment(plan: Plan): Promise<void> {
    if (startingRef.current) return;
    startingRef.current = true;
    try {
      await runStartPayment(plan);
    } finally {
      startingRef.current = false;
    }
  }

  async function runStartPayment(plan: Plan): Promise<void> {
    const token = tokenRef.current;
    if (!token) return;
    // Con cupón NO se pide documento ni se abre el widget: el canje reemplaza
    // el pago por completo (el documento solo lo exige Line).
    if (couponCode.trim()) {
      // Verificación local (formato + dígito verificador): un código mal
      // tipeado se rechaza al instante, sin pegarle al backend.
      const code = precheckCoupon(couponCode);
      if (!code) {
        setErr('Código no válido. Revisá que lo hayas copiado bien.');
        return;
      }
      setLoading(true);
      setErr(null);
      try {
        const res = await redeemCoupon({ bearer: token }, plan.plan_id, code);
        clearCheckoutResume(resumeKeyRef.current);
        returnToOtt(
          'success',
          `Tu plan quedó activo por ${res.free_days} días gratis.`,
        );
      } catch (e2) {
        setErr(couponErrorMessage(e2));
      } finally {
        setLoading(false);
      }
      return;
    }
    const doc = customerDoc.replace(/\D/g, '');
    if (doc.length < 6) {
      setErr('Ingresá un número de documento válido (solo números).');
      return;
    }
    setLoading(true);
    setErr(null);
    try {
      const back = new URL(`${window.location.origin}/${partnerId}/pay`);
      back.searchParams.set('mp_return', '1');
      const session = await createCheckoutSession(
        { bearer: token },
        plan.plan_id,
        { document: doc },
        { successUrl: back.toString() },
      );
      // Pasarela con redirect (MP): su checkout no se puede embeber. Vuelve a
      // /pay, que confirma (pidiendo login: la sesión corta de /subscribe no
      // sobrevive al ir y volver).
      const outcome = checkoutOutcome(session, REDIRECT_PROVIDERS);
      if (outcome.kind === 'activated') {
        // Un pago anterior ya estaba aprobado: no hay nada que pagar.
        finishActivated();
        return;
      }
      if (outcome.kind === 'redirect') {
        window.location.href = outcome.url;
        return;
      }
      if (outcome.kind === 'invalid') {
        setErr('No pudimos iniciar el pago. Probá de nuevo en unos minutos.');
        return;
      }
      // Se guarda ANTES de mostrar el widget: desde acá el pago puede aprobarse
      // sin que esta pestaña llegue a enterarse.
      saveCheckoutResume(resumeKeyRef.current, {
        paymentId: outcome.paymentId,
        planId: plan.plan_id,
        checkoutUrl: outcome.checkoutUrl,
      });
      setStep({ kind: 'widget', plan, checkoutUrl: outcome.checkoutUrl, paymentId: outcome.paymentId });
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : 'No pudimos iniciar el pago.');
    } finally {
      setLoading(false);
    }
  }

  function finishActivated(): void {
    clearCheckoutResume(resumeKeyRef.current);
    returnToOtt('success', 'Tu plan quedó activo.');
  }

  // La pasarela rechazó el pago: ya no hay nada que confirmar. Vuelve al paso
  // de pago con el aviso. (En un cambio de plan la suscripción anterior sigue
  // viva: por eso el rechazo se mira antes que el `status` — ver confirmVerdict.)
  function showRejected(plan: Plan): void {
    clearCheckoutResume(resumeKeyRef.current);
    setErr(PAYMENT_REJECTED_MESSAGE);
    setStep({ kind: 'pay', plan });
  }

  // Confirma el pago: sondea hasta un veredicto (activado o rechazado). Si se
  // agota sin veredicto NO ofrece pagar de nuevo: pasa a `verifying`, que
  // vuelve a consultar el MISMO pago.
  async function confirmPayment(
    plan: Plan,
    paymentId: string,
    origin: 'resume' | 'timeout' = 'timeout',
  ): Promise<void> {
    const token = tokenRef.current;
    if (!token) return;
    setStep({ kind: 'confirming', checking: origin === 'resume' });
    const result = await pollConfirm(
      () => confirmCheckoutPayment({ bearer: token }, plan.plan_id, paymentId),
      origin === 'resume' ? RESUME_CONFIRM_DELAYS : CONFIRM_DELAYS,
    );
    if (result.kind === 'activated') {
      finishActivated();
      return;
    }
    if (result.kind === 'rejected') {
      showRejected(plan);
      return;
    }
    if (result.kind === 'unauthorized') {
      setStep({ kind: 'error', message: 'Tu sesión expiró. Volvé a empezar.' });
      return;
    }
    setStep({ kind: 'verifying', plan, paymentId, origin });
  }

  // Salir del pago en curso (volver a la grilla, pagar con otra tarjeta,
  // reintentar tras un fallo del widget): antes se lo consulta una vez. Solo
  // si no está aprobado se lo suelta.
  async function leavePaymentSafely(
    plan: Plan,
    paymentId: string,
    next: 'manage' | 'pay' = 'manage',
  ): Promise<void> {
    const token = tokenRef.current;
    if (!token) return;
    setStep({ kind: 'confirming', checking: true });
    const result = await pollConfirm(
      () => confirmCheckoutPayment({ bearer: token }, plan.plan_id, paymentId),
      SINGLE_CONFIRM,
    );
    if (result.kind === 'activated') {
      finishActivated();
      return;
    }
    if (result.kind === 'rejected') {
      showRejected(plan);
      return;
    }
    if (result.kind === 'unauthorized') {
      setStep({ kind: 'error', message: 'Tu sesión expiró. Volvé a empezar.' });
      return;
    }
    // Sin aprobar: si ese pago igual se aprueba después, el checkout siguiente
    // lo detecta y activa sin cobrar.
    clearCheckoutResume(resumeKeyRef.current);
    setErr(null);
    setStep(next === 'pay' ? { kind: 'pay', plan } : { kind: 'manage', current: currentRef.current });
  }

  // Chequeo de fondo de la pantalla `verifying`: una consulta silenciosa que
  // solo actúa si hay veredicto.
  async function backgroundCheck(plan: Plan, paymentId: string): Promise<void> {
    const token = tokenRef.current;
    if (!token) return;
    const result = await pollConfirm(
      () => confirmCheckoutPayment({ bearer: token }, plan.plan_id, paymentId),
      SINGLE_CONFIRM,
    );
    if (result.kind === 'activated') finishActivated();
    else if (result.kind === 'rejected') showRejected(plan);
  }

  async function doCancel(): Promise<void> {
    const token = tokenRef.current;
    if (!token) return;
    setLoading(true);
    setErr(null);
    try {
      const res = await cancelSubscription(token);
      returnToOtt(
        'success',
        res.access_until
          ? `Tu suscripción no se va a renovar. Seguís con tu plan hasta el ${fmtDate(res.access_until)}; después pasás al plan gratuito.`
          : 'Tu suscripción fue cancelada.',
      );
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : 'No pudimos cancelar.');
    } finally {
      setLoading(false);
    }
  }

  // Widget postMessage. El origin esperado sale de la checkout_url del backend
  // (staging/prod según la config del provider).
  useEffect(() => {
    if (step.kind !== 'widget') return;
    const expectedOrigin = lineWidgetOrigin(step.checkoutUrl);
    function onMessage(event: MessageEvent): void {
      if (event.origin !== expectedOrigin) return;
      const type = (event.data as { type?: string })?.type;
      if (step.kind !== 'widget') return;
      if (type === 'line-checkout:payment-success') {
        void confirmPayment(step.plan, step.paymentId);
      } else if (type === 'line-checkout:payment-failure') {
        setStep({
          kind: 'error',
          message: 'El pago no pudo completarse. Podés reintentar.',
          canRetry: true,
          // El reintento confirma este pago antes de abrir otro.
          inFlight: { plan: step.plan, paymentId: step.paymentId },
        });
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.kind]);

  // ----- Render -----
  if (step.kind === 'loading') {
    return (
      <AuthShell title="Un momento…" subtitle="Cargando tu suscripción.">
        <div className="text-center text-sm text-text-muted py-4">⏳</div>
      </AuthShell>
    );
  }

  if (step.kind === 'mismatch') {
    return (
      <AccountMismatchScreen
        reason={step.reason}
        onLogin={switchAccount}
        flowExit={<FlowExitLink onExit={() => returnToOtt('cancelled', 'Volviste sin cambios.')} />}
      />
    );
  }

  // Una acción espera el "sí, es mi cuenta". Tapa la pantalla desde la que se
  // la pidió (grilla o paso de pago); al responder se vuelve a ella.
  if (pending && (step.kind === 'manage' || step.kind === 'pay')) {
    return (
      <AccountConfirmScreen
        hint={account?.hint ?? null}
        onConfirm={() => {
          acknowledgedRef.current = true;
          setPending(null);
          pending.run();
        }}
        onSwitch={switchAccount}
        onBack={() => setPending(null)}
      />
    );
  }

  if (step.kind === 'login') {
    return (
      <AuthShell
        title="Gestioná tu plan"
        subtitle="Ingresá para ver y cambiar tu suscripción."
      flowExit={<FlowExitLink onExit={() => returnToOtt('cancelled', 'Volviste sin cambios.')} />}
      >
        <form onSubmit={onLogin} className="space-y-4" noValidate aria-busy={loading || undefined}>
          <FormField name="email" type="email" label="Email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <FormField name="password" type="password" label="Contraseña" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          {err ? <div role="alert" className="rounded-lg border border-error/30 bg-error/10 p-3 text-sm text-error">{err}</div> : null}
          <SubmitButton loading={loading} loadingLabel="Ingresando…">Continuar</SubmitButton>
        </form>
      </AuthShell>
    );
  }

  if (step.kind === 'manage') {
    const current = step.current;
    // Solo un plan VIGENTE lleva la chapita "Plan actual" (y viene marcado, con
    // el botón en "Conservar plan"). Con la suscripción vencida o cancelada no
    // hay plan actual que conservar: volver a contratar el mismo plan es
    // exactamente lo que el usuario viene a hacer.
    const activePlanId = current && LIVE.includes(current.status) ? current.plan.plan_id : null;
    const until = endsAt(current);
    return (
      <AuthShell
        title="Planes"
        subtitle="Seleccioná un plan para continuar."
      flowExit={<FlowExitLink onExit={() => returnToOtt('cancelled', 'Volviste sin cambios.')} />}
      >
        <div className="space-y-4">
          <AccountLine hint={account?.hint ?? null} onSwitch={switchAccount} />
          {current && until ? (
            <div role="status" className="rounded-lg border border-border p-3 text-sm text-text-muted">
              Tu plan {current.plan.display_name} no se va a renovar. Lo seguís teniendo hasta el{' '}
              {fmtDate(until)}; después pasás al plan gratuito.
            </div>
          ) : null}
          {/* El plan vigente NO tiene cartel propio arriba: es una card más de
              la grilla, con la chapita "Plan actual". La grilla llega con una
              opción marcada —el vigente, o el recomendado si no tiene ninguno—
              así que el usuario puede ir directo al botón. */}
          <PlanPicker
            partnerId={partnerId}
            value={selected?.plan_id ?? null}
            onChange={(_id, plan) => {
              setSelected(plan);
              setErr(null);
            }}
            currentPlanId={activePlanId}
            preferredPlanId={preselectPlanId}
            autoSelect
          />
          {err ? <div role="alert" className="rounded-lg border border-error/30 bg-error/10 p-3 text-sm text-error">{err}</div> : null}
          <SubmitButton type="button" onClick={() => void applySelection()} disabled={!selected} loading={loading} loadingLabel="Aplicando…">
            {selected && activePlanId === selected.plan_id ? 'Conservar plan' : 'Activalo ahora'}
          </SubmitButton>
          {current && LIVE.includes(current.status) && current.plan.price_amount > 0 && !until ? (
            <div className="text-sm">
              <button type="button" onClick={() => guarded(() => void doCancel())} className="text-error underline hover:opacity-80">
                Cancelar suscripción
              </button>
            </div>
          ) : null}
          {/* Salida del circuito. Usa la función de la pantalla (no la URL
              armada acá) porque el return_url se valida server-side contra la
              whitelist del client, y si no pasa hay que caer a la pantalla
              final en vez de redirigir a cualquier lado. */}
        </div>
      </AuthShell>
    );
  }

  if (step.kind === 'pay') {
    const { plan } = step;
    return (
      <AuthShell
        title={`Activá ${plan.display_name}`}
        subtitle="Un último paso."
      flowExit={<FlowExitLink onExit={() => returnToOtt('cancelled', 'Volviste sin cambios.')} />}
      >
        <div className="space-y-4">
          <div className="rounded-xl border border-border p-4">
            <div className="text-sm text-text-muted">{plan.display_name}</div>
            <div className="mt-1 text-2xl font-semibold text-text">
              {fmt(plan.price_amount, plan.price_currency)}<span className="text-sm text-text-muted">/mes</span>
            </div>
          </div>
          <FormField name="document" type="text" inputMode="numeric" label="Número de documento" autoComplete="off" required value={customerDoc} onChange={(e) => setCustomerDoc(e.target.value)} />
          <FormField name="coupon" type="text" label="Cupón (opcional)" autoComplete="off" value={couponCode} onChange={(e) => { setCouponCode(e.target.value); setErr(null); }} />
          {err ? <div role="alert" className="rounded-lg border border-error/30 bg-error/10 p-3 text-sm text-error">{err}</div> : null}
          <AccountLine hint={account?.hint ?? null} onSwitch={switchAccount} />
          {/* Pago y canje de cupón: los dos pasan por el guard de la cuenta. */}
          <SubmitButton type="button" onClick={() => guarded(() => void startPayment(plan))} loading={loading} loadingLabel={couponCode.trim() ? 'Validando el cupón…' : 'Abriendo el pago…'}>
            {couponCode.trim() ? 'Validar cupón' : 'Activar'}
          </SubmitButton>
          {/* Quien entró por `checkout_plan_id` nunca vio la grilla: para él el
              link no es "volver" a ningún lado, es "elegir otro". */}
          <div className="text-center">
            <BackStepLink
              onClick={() => setStep({ kind: 'manage', current: currentRef.current })}
              {...(checkoutPlanId ? { label: 'Elegir otro plan' } : {})}
            />
          </div>
        </div>
      </AuthShell>
    );
  }

  if (step.kind === 'widget') {
    // Tema del widget: paleta ACTIVA del portal (clara u oscura), igual que en
    // Pay.tsx — Line pinta el body del documento con estos params.
    const checkoutSrc =
      brandingState.status === 'ready'
        ? withLineTheme(
            step.checkoutUrl,
            paletteToLineTheme(resolvePalette(brandingState.branding, colorScheme)),
          )
        : step.checkoutUrl;
    return (
      <main className="min-h-[100dvh] flex flex-col bg-background px-3 py-3 sm:px-4">
        <div className="mx-auto flex min-h-0 w-full max-w-md flex-1 flex-col gap-3">
          <h1 className="shrink-0 text-center text-lg font-semibold text-text">Pagá de forma segura</h1>
          {/* Sin "No es mi cuenta" acá: hay un pago abierto y no se lo suelta sin
              consultarlo. La salida es "Volver al paso anterior", que lo
              verifica; desde ahí está el enlace. */}
          <div className="shrink-0">
            <AccountLine hint={account?.hint ?? null} />
          </div>
          <iframe title="Line Checkout" src={checkoutSrc} className="w-full flex-1 rounded-lg border border-border bg-background" style={{ minHeight: 520 }} />
          {/* El widget de pago ocupa la pantalla: acá las dos salidas van juntas
              al pie, con el mismo peso apagado. */}
          <div className="shrink-0 space-y-1 py-1 text-center">
            <BackStepLink
              onClick={() => void leavePaymentSafely(step.plan, step.paymentId)}
              label="¿Algo salió mal? Volver al paso anterior"
            />
            <FlowExitLink onExit={() => returnToOtt('cancelled', 'Volviste sin cambios.')} />
          </div>
        </div>
      </main>
    );
  }

  if (step.kind === 'confirming') {
    return (
      <AuthShell
        title={step.checking ? 'Verificando tu pago…' : 'Confirmando tu pago…'}
        subtitle="No cierres esta ventana."
      >
        <div className="text-center text-sm text-text-muted py-4">⏳</div>
      </AuthShell>
    );
  }

  if (step.kind === 'verifying') {
    const { plan, paymentId } = step;
    return (
      <PaymentVerifying
        origin={step.origin}
        onVerify={() => void confirmPayment(plan, paymentId)}
        onPayAgain={() => void leavePaymentSafely(plan, paymentId, 'pay')}
        onBackgroundCheck={() => void backgroundCheck(plan, paymentId)}
      />
    );
  }

  if (step.kind === 'redirecting') {
    return (
      <AuthShell title="¡Listo! ✓" subtitle="Te llevamos de vuelta…">
        <div className="text-center text-sm text-text-muted py-4">⏳</div>
      </AuthShell>
    );
  }

  if (step.kind === 'done') {
    return (
      <AuthShell title="Listo ✓" subtitle="Ya podés disfrutar del fútbol argentino.">
        <div className="rounded-lg border border-primary/30 bg-primary/10 p-4 text-sm text-text">
          {step.message} Podés volver a la app.
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Hubo un problema" subtitle="No pudimos completar la operación.">
      <div role="alert" className="rounded-lg border border-error/30 bg-error/10 p-3 text-sm text-error">{step.message}</div>
      {step.canRetry ? (
        <SubmitButton
          type="button"
          onClick={() => {
            const inFlight = step.inFlight;
            if (inFlight) void leavePaymentSafely(inFlight.plan, inFlight.paymentId);
            else setStep({ kind: 'manage', current: currentRef.current });
          }}
          loading={loading}
          loadingLabel="…"
        >
          Volver a intentar
        </SubmitButton>
      ) : null}
    </AuthShell>
  );
}
